type _AlMsg = import("@components/AlertMessage").AlertMessageProps;

// #region ##################################################################################### ESSENTIALS
// ---------------------------------------------------------------------- ONLY KEYS
/**
 * Selecciona todas las propiedades de un objeto, sin importar el tipo de dato.
 */
export type OnlyKeys<T> = { [P in keyof T]?: any };

// ---------------------------------------------------------------------- AUTO KEYS
/**
 * Selecciona todas las propiedades y permite agregar nuevas.
 */
export type AutoKeys<T> = OnlyKeys<T> & { [key: string]: any };

// ---------------------------------------------------------------------- _SETUP
/**
 * Clase extensible que coloca `props` automáticamente.
 * 1. {@link id} - ***readonly*** `string | null`.
 */
abstract class _setup {
  /**
   * `string | null` - ID del elemento.
   *
   * Si proviene de `Firestore`, entonces se refiere al ID del documento.
   *
   * Todos los elementos tienen un ID aún si no se necesita.
   * */
  readonly id: string | null = null;

  /**
   * Coloca todos los props automáticamente.
   * @param ini Objeto de donde obtener los datos iniciales.
   *
   * 1. Puede estar vacío y puede omitir algunos campos.
   * 2. Se toma el valor por defecto de las propiedades que no se incluyan.
   * 3. Si el tipo de dato de las propiedades no coincide, se ignora.
   * 4. Se pueden incluir propiedades de más.
   * 5. Si el campo actual es `null`, entonces acepta **cualquier valor** (`null <-- any`).
   * 6. Es **imposible colocar `null`** a un campo que ya exista (`any <-/- null`).
   */
  protected update(ini?: AutoKeys<this>) {
    if (ini === undefined) return;
    for (const key in this) {
      const that = ini[key];
      if (that === undefined || that === null) continue;
      if (
        this[key] === null ||
        (typeof that === typeof this[key] &&
          (typeof that !== "object" ||
            Object.getPrototypeOf(that) === Object.getPrototypeOf(this[key])))
      ) {
        this[key as string] = that;
      }
    }
  }

  /**
   * Coloca todos los props automáticamente.
   * @param ini Objeto de donde obtener los datos iniciales.
   *
   * 1. Puede estar vacío y puede omitir algunos campos.
   * 2. Se toma el valor por defecto de las propiedades que no se incluyan.
   * 3. Si el tipo de dato de las propiedades no coincide, se ignora.
   * 4. Se pueden incluir propiedades de más.
   * 5. Si el campo actual es `null`, entonces acepta **cualquier valor** (`null <-- any`).
   * 6. Es **imposible colocar `null`** a un campo que ya exista (`any <-/- null`).
   */
  constructor(ini?: AutoKeys<_setup>) {
    this.update(ini);
  }
}

// ---------------------------------------------------------------------- _BASE
/**
 * Provides basic prop structure for functional components
 */
export type _Base = {
  /** Overrides any other `className` passed. */
  className?: string;
  /** Overrides any other `style` passed. */
  _style?: import("react").CSSProperties;
};
// #endregion

// #region ##################################################################################### GLOBAL STATE
/**
 * Modelo para la variable global a utilizar en todo el sistema. Esta se creó con el propósito de minimizar las llamadas
 * a la base de datos (Firebase) y acceder a la información relevante más fácilmente.
 *
 * 1. {@link currentUser} - `Usuario | null`.
 * 1. {@link parroquia} - `Parroquia`.
 * 1. {@link servicios} - `Servicios`.
 * 1. {@link cache} - `any`.
 * 1. {@link firstTime} - `boolean`.
 * 1. {@link alert} - `AlertMessageProps`.
 * 1. {@link alertRef} - `() => void`.
 * 1. {@link setAlert} - ***readonly*** `() => void`.
 * 1. {@link DB} - `IDBDatabase | null`.
 */
export class Global {
  /** `Usuario | null` - Representa el usuario con la sesión iniciada actualmente. También describe sus permisos. */
  currentUser: Usuario | null = null;
  /** `Parroquia` - Representa la parroquia actual, para realizar modificaciones. */
  parroquia: Parroquia = new Parroquia();
  /** `Servicios` - Representa las sub-colecciones de Firebase alojadas dentro de la parroquia. */
  servicios: Servicios = {};
  /** `any` - Guarda cualquier clase de información, puede estar vacío y no es confiable. */
  cache: any = {};
  /** `() => void` - Representa una función para refrescar toda la aplicación desde la raíz. */
  refresh: () => void = () => {};
  /** `boolean` - Representa si es la primera vez que carga la aplicación desde el inicio. */
  firstTime: boolean = true;
  /** `IndexedDB` - Referencia al objeto de la base de datos local para manipular info más fácil. */
  DB: IDBDatabase | null = null;
  /**
   * `AlertMessageProps` - Parámetros para accionar el `AlertMessage`:
   *
   * 1. **`_type?`**: `"error"` | `"warning"` | `"success"` | `"informative"`; Color de la alerta, default: `"success"`.
   * 2. **`_message?`**: `string`; Mensaje de alerta a mostrar. Si no hay nada no se muestra.
   * 3. **`_timer?`**: `number`; Tiempo de espera (ms) para ocultar el mensaje automáticamente, default: `5000`.
   * 4. **`_hideButton?`**: `boolean`; Indica si es que dispondrá de un botón para ocultar el mensaje o no, default: `true`.
   */
  alert: _AlMsg = {};
  /** `() => void` - Función para accionar manualmente las alertas. */
  alertRef: () => void = () => {
    console.log((this.alert._type || "Alerta") + ":\n" + this.alert._message);
  };
  /** Actualiza las alertas y las ejecuta `alert = param` + `alertRef()`. */
  readonly setAlert = (p: _AlMsg) => {
    this.alert = p;
    this.alertRef();
  };
}
// #endregion

// #region ##################################################################################### DATABASE MODELS
// ---------------------------------------------------------------------- DB
/**
 * Modela toda la base de datos dentro de Firebase.
 *
 * 1. {@link id} - ***readonly*** `string | null`.
 * 1. {@link usuarios} - `Usuario[]`.
 * 1. {@link parroquias} - `Parroquia[]`.
 */
export class db extends _setup {
  /** `Usuario[]` - Representa todos los usuarios registrados junto a sus permisos. */
  usuarios: Usuario[] = [];
  /** `Parroquias[]` - Representa todas las parroquias registradas junto a sus servicios. */
  parroquias: Parroquia[] = [];

  /**
   * Coloca todos los props automáticamente.
   * @param ini Objeto de donde obtener los datos iniciales.
   *
   * 1. Puede estar vacío y puede omitir algunos campos.
   * 2. Se toma el valor por defecto de las propiedades que no se incluyan.
   * 3. Si el tipo de dato de las propiedades no coincide, se ignora.
   * 4. Se pueden incluir propiedades de más.
   */
  constructor(ini?: AutoKeys<db>) {
    super(ini);
    this.update(ini);
  }
}

// ---------------------------------------------------------------------- SERVICIOS
/**
 * Servicios disponibles para una parroquia, estos campos representan sub-colecciones dentro de Firestore.
 *
 * 1. {@link usuarios} - `Usuario[]`.
 * 1. {@link bautizos} - `Bautizo[]`.
 * 1. {@link comuniones} - `Comunion[]`.
 * 1. {@link confirmaciones} - `Confirmacion[]`.
 * 1. {@link matrimonios} - `Matrimonio[]`.
 * 1. {@link nichos} - `Nicho[]`.
 * 1. {@link secciones} - `Seccion[]`.
 * 1. {@link pagos} - `Pago[]`.
 * 1. {@link empleados} - `Empleado[]`.
 * 1. {@link cenizas} - `Ceniza[]`.
 * 1. {@link expedientes} - `Expediente[]`.
 * 1. {@link beneficiarios} - `Beneficiario[]`.
 */
export type Servicios = {
  // GENERAL
  /** `Usuario[]` - Contiene información sobre todos los usuarios dentro de la parroquia. */
  usuarios?: Usuario[];

  // SACRAMENTOS
  /** `Bautizo[]?` - Contiene información sobre todos los bautismos dentro de la parroquia. */
  bautizos?: Bautizo[];
  /** `Comunion[]?` - Contiene información sobre todas las comuniones dentro de la parroquia. */
  comuniones?: Comunion[];
  /** `Confirmacion[]?` - Contiene información sobre todas las confirmaciones dentro de la parroquia. */
  confirmaciones?: Confirmacion[];
  /** `Matrimonio[]?` - Contiene información sobre todos los matrimonios dentro de la parroquia. */
  matrimonios?: Matrimonio[];

  // NICHOS
  /** `Nicho[]?` - Contiene información sobre todos los nichos dentro de la parroquia. */
  nichos?: Nicho[];
  /** `Seccion[]?` - Se refiere a las secciones de nichos. */
  secciones?: Seccion[];
  /** `Pago[]?` - Se refiere a los pagos hacia el área de nichos. */
  pagos?: Pago[];
  /** `Empleado[]?` - Son empleados que trabajan (o trabajaron) en el área de nichos. */
  empleados?: Empleado[];
  /** `Ceniza[]?` - Contiene información sobre todas las cenizas depositadas en los nichos. */
  cenizas?: Ceniza[];
  /** `Expediente[]?` - Contiene información sobre todos los expedientes emitidos desde el área de nichos. */
  expedientes?: Expediente[];
  /** `Beneficiario[]?` - Contiene información tanto de beneficiarios como titulares de nichos. */
  beneficiarios?: Beneficiario[];
};

// ---------------------------------------------------------------------- USUARIO
/**
 * Modelo de sesiones en el sistema. Esto no necesita de `Firebase.Auth` porque las sesiones se validan con información
 * de la base de datos, y la sesión prevalece mientras la pestaña esté activa.
 *
 * Para poder acceder a los servicios de una determinada parroquia, lo que se necesita es primeramente el email registrado
 * de dicha parroquia (que aparece en la colección `parroquias`), después necesitamos un nombre de usuario y su contraseña.
 *
 * Para saber si la sesión está autenticada, basta con fijarnos si posee un ID que no sea NULL.
 *
 * 1. {@link id} - ***readonly*** `string | null`.
 * 1. {@link user} - `string`.
 * 1. {@link permiso_nichos} - `number`.
 * 1. {@link permiso_sacramentos} - `number`.
 * 1. {@link notas} - `string`.
 */
export class Usuario extends _setup {
  /** `string` - Nombre de usuario para iniciar sesión. */
  user: string = "";
  /** `number` - Determina si tiene permiso para ver y escribir según: `-1` = ninguno, `0` = lectura, `1` = escritura. */
  permiso_nichos: number = -1;
  /** `number` - Determina si tiene permiso para ver y escribir según: `-1` = ninguno, `0` = lectura, `1` = escritura. */
  permiso_sacramentos: number = -1;

  /** `string` - Cualquier consideración o notas adicionales. */
  notas: string = "";

  /**
   * Coloca todos los props automáticamente.
   * @param ini Objeto de donde obtener los datos iniciales.
   *
   * 1. Puede estar vacío y puede omitir algunos campos.
   * 2. Se toma el valor por defecto de las propiedades que no se incluyan.
   * 3. Si el tipo de dato de las propiedades no coincide, se ignora.
   * 4. Se pueden incluir propiedades de más.
   */
  constructor(ini?: AutoKeys<Usuario>) {
    super(ini);
    this.update(ini);
  }
}

// ---------------------------------------------------------------------- PARROQUIAS
/**
 * Modelo de parroquia. Esta es la clase más importante por ser la principal que modela la estructura de todas las parroquias
 * registradas. Teóricamente, el sistema se cobraría por los servicios "instalados" dentro de la parroquia.
 *
 * Los servicios básicos son:
 * 1. Sacramentos
 *    -  Bautizos [COL]
 *    -  Comuniones [COL]
 *    -  Confirmaciones [COL]
 *    -  Matrimonios [COL]
 * 2. Nichos
 *    - Nichos [COL]
 *    - Secciones [COL]
 *    - Pagos [COL]
 *    - Empleados [COL]
 *    - Cenizas [COL]
 *    - Expedientes [COL]
 *    - Beneficiarios [COL]
 *    - Documentos [PROP]
 *    - Tipos_Nichos [PROP]
 *
 * Propiedades:
 * 1. {@link id} - ***readonly*** `string | null`.
 * 1. {@link nombre} - `string`.
 * 1. {@link parroco_encargado} - `string`.
 * 1. {@link presbiteros} - `string[]`.
 * 1. {@link ciudad} - `string`.
 * 1. {@link direccion} - `string`.
 * 1. {@link telefono} - `string`.
 * 1. {@link email} - `string`.
 * 1. {@link nichos} - `boolean`.
 * 1. {@link sacramentos} - `boolean`.
 * 1. {@link notas} - `string`.
 * 1. {@link documentos} - `string[]`.
 * 1. {@link tipos_nichos} - `Tipo_Nicho[]`.
 */
export class Parroquia extends _setup {
  /** `string` - Nombre formal de la parroquia. */
  nombre: string = "";
  /** `string` - Nombre del párroco encargado de la parroquia. */
  parroco_encargado: string = "";
  /** `string[]` - Nombres de los presbíteros aplicables para la parroquia. */
  presbiteros: string[] = [];

  /** `string` - Ciudad de la parroquia en cualquier formato. */
  ciudad: string = "";
  /** `string` - Dirección de la parroquia en cualquier formato. */
  direccion: string = "";
  /** `string` - Teléfono de la parroquia. */
  telefono: string = "";
  /** `string` - Email de la parroquia, esto es para poder acceder como usuario. */
  email: string = "";

  /** `boolean` - Si es que la parroquia cuenta con el servicio para **Nichos** */
  nichos: boolean = false;
  /** `boolean` - Si es que la parroquia cuenta con el servicio para **Sacramentos** */
  sacramentos: boolean = false;
  /** `string` - Cualquier consideración o notas adicionales. */
  notas: string = "";

  /** `string[]?` - Lista de documentos requeridos para las cenizas (listado de únicamente los nombres). */
  documentos: string[] = [];
  /** `Tipo_Nicho[]?` - Listado de los tipos de nichos existentes dentro de la parroquia. */
  tipos_nichos: Tipo_Nicho[] = [];

  /**
   * Coloca todos los props automáticamente.
   * @param ini Objeto de donde obtener los datos iniciales.
   *
   * 1. Puede estar vacío y puede omitir algunos campos.
   * 2. Se toma el valor por defecto de las propiedades que no se incluyan.
   * 3. Si el tipo de dato de las propiedades no coincide, se ignora.
   * 4. Se pueden incluir propiedades de más.
   */
  constructor(ini?: AutoKeys<Parroquia>) {
    super(ini);
    this.update(ini);
  }
}
// #endregion

// #region ##################################################################################### SACRAMENTOS
// ---------------------------------------------------------------------- SACRAMENTO (base)
/**
 * Contiene información general de cualquier sacramento.
 *
 * 1. {@link id} - ***readonly*** `string | null`.
 * 1. {@link fecha_sacramento} - `Date`.
 * 1. {@link presbitero} - `string`.
 * 1. {@link libro} - `number`.
 * 1. {@link pagina} - `number`.
 * 1. {@link partida} - `number`.
 * 1. {@link notas} - `string`.
 */
export abstract class Sacramento extends _setup {
  /** `Date` - Fecha cuando se ejerció el sacramento. */
  fecha_sacramento: Date = new Date();
  /** `string` - Nombre completo del presbítero a cargo, separado con espacios donde sea necesario. */
  presbitero: string = "";

  /** `number` - Representa el número del libro según el sacramento. */
  libro: number = 0;
  /** `number` - Representa el número de página según el libro. */
  pagina: number = 0;
  /** `number` - Representa el número de partida según la página. */
  partida: number = 0;
  /** `string` - Cualquier consideración o notas adicionales. */
  notas: string = "";

  /**
   * Coloca todos los props automáticamente.
   * @param ini Objeto de donde obtener los datos iniciales.
   *
   * 1. Puede estar vacío y puede omitir algunos campos.
   * 2. Se toma el valor por defecto de las propiedades que no se incluyan.
   * 3. Si el tipo de dato de las propiedades no coincide, se ignora.
   * 4. Se pueden incluir propiedades de más.
   */
  constructor(ini?: AutoKeys<Sacramento>) {
    super(ini);
    this.update(ini);
  }
}

// ---------------------------------------------------------------------- BAUTIZOS
/**
 * Contiene toda la información relevante a los bautismos.
 *
 * 1. {@link id} - ***readonly*** `string | null`.
 * 1. {@link nombre} - `string`.
 * 1. {@link sexo} - `boolean`.
 * 1. {@link fecha_nacimiento} - `Date`.
 * 1. {@link ciudad_nacimiento} - `string`.
 * 1. {@link folio_acta_nacimiento} - `string`.
 * 1. {@link padre} - `string`.
 * 1. {@link madre} - `string`.
 * 1. {@link abuelos_paternos} - `string`.
 * 1. {@link abuelos_maternos} - `string`.
 * 1. {@link padrino} - `string`.
 * 1. {@link madrina} - `string`.
 * 1. {@link notas_marginales} - `string`.
 * 1. {@link fecha_sacramento} - `Date`.
 * 1. {@link presbitero} - `string`.
 * 1. {@link libro} - `number`.
 * 1. {@link pagina} - `number`.
 * 1. {@link partida} - `number`.
 * 1. {@link notas} - `string`.
 */
export class Bautizo extends Sacramento {
  /** `string` - Nombre completo del bebé, separado con espacios donde sea necesario. */
  nombre: string = "";
  /** `boolean` - Sexo del bebé según: `false` para hombre, `true` para mujer. */
  sexo: boolean = false;
  /** `Date` - Fecha de nacimiento del bebé. */
  fecha_nacimiento: Date = new Date();
  /** `string` - Ciudad y lugar de nacimiento. */
  ciudad_nacimiento: string = "";
  /** `string` - Folio del acta de nacimiento según como aparezca en el registro civil. */
  folio_acta_nacimiento: string = "";

  /** `string` - Nombre completo del padre, separado con espacios donde sea necesario. */
  padre: string = "";
  /** `string` - Nombre completo de la madre, separado con espacios donde sea necesario. */
  madre: string = "";
  /** `string` - Nombre completo de los abuelos paternos, separado con espacios y coma donde sea necesario. */
  abuelos_paternos: string = "";
  /** `string` - Nombre completo de los abuelos maternos, separado con espacios y coma donde sea necesario. */
  abuelos_maternos: string = "";
  /** `string` - Nombre completo del padrino, separado con espacios donde sea necesario. */
  padrino: string = "";
  /** `string` - Nombre completo de la madrina, separado con espacios donde sea necesario. */
  madrina: string = "";

  /** `string` - Notas marginales aplicables. */
  notas_marginales: string = "";

  /**
   * Coloca todos los props automáticamente.
   * @param ini Objeto de donde obtener los datos iniciales.
   *
   * 1. Puede estar vacío y puede omitir algunos campos.
   * 2. Se toma el valor por defecto de las propiedades que no se incluyan.
   * 3. Si el tipo de dato de las propiedades no coincide, se ignora.
   * 4. Se pueden incluir propiedades de más.
   */
  constructor(ini?: AutoKeys<Bautizo>) {
    super(ini);
    this.update(ini);
  }
}

// ---------------------------------------------------------------------- COMUNIONES
/**
 * Contiene toda la información relevante a las comuniones.
 *
 * 1. {@link id} - ***readonly*** `string | null`.
 * 1. {@link nombre} - `string`.
 * 1. {@link sexo} - `boolean`.
 * 1. {@link padre} - `string`.
 * 1. {@link madre} - `string`.
 * 1. {@link padrino_madrina} - `string`.
 * 1. {@link ciudad_bautizo} - `string`.
 * 1. {@link parroquia_bautizo} - `string`.
 * 1. {@link fecha_bautizo} - `Date`.
 * 1. {@link fecha_sacramento} - `Date`.
 * 1. {@link presbitero} - `string`.
 * 1. {@link libro} - `number`.
 * 1. {@link pagina} - `number`.
 * 1. {@link partida} - `number`.
 * 1. {@link notas} - `string`.
 */
export class Comunion extends Sacramento {
  /** `string` - Nombre completo de quien recibe el sacramento, separado con espacios donde sea necesario. */
  nombre: string = "";
  /** `boolean` - Sexo del bebé según: `false` para hombre, `true` para mujer. */
  sexo: boolean = false;

  /** `string` - Nombre completo del padre, separado con espacios donde sea necesario. */
  padre: string = "";
  /** `string` - Nombre completo de la madre, separado con espacios donde sea necesario. */
  madre: string = "";
  /** `string` - Nombre completo del padrino o madrina, separado con espacios donde sea necesario. */
  padrino_madrina: string = "";

  /** `string` - Ciudad y lugar donde se recibió el bautismo. */
  ciudad_bautizo: string = "Hermosillo, Sonora";
  /** `string` - Parroquia donde se recibió el bautismo. */
  parroquia_bautizo: string = "";
  /** `Date` - Fecha de cuando se recibió el bautismo. */
  fecha_bautizo: Date = new Date();

  /**
   * Coloca todos los props automáticamente.
   * @param ini Objeto de donde obtener los datos iniciales.
   *
   * 1. Puede estar vacío y puede omitir algunos campos.
   * 2. Se toma el valor por defecto de las propiedades que no se incluyan.
   * 3. Si el tipo de dato de las propiedades no coincide, se ignora.
   * 4. Se pueden incluir propiedades de más.
   */
  constructor(ini?: AutoKeys<Comunion>) {
    super(ini);
    this.update(ini);
  }
}

// ---------------------------------------------------------------------- CONFIRMACIONES
/**
 * Contiene toda la información relevante a las confirmaciones.
 *
 * 1. {@link id} - ***readonly*** `string | null`.
 * 1. {@link nombre} - `string`.
 * 1. {@link sexo} - `boolean`.
 * 1. {@link padre} - `string`.
 * 1. {@link madre} - `string`.
 * 1. {@link padrino_madrina} - `string`.
 * 1. {@link parroquia_bautizo} - `string`.
 * 1. {@link ciudad_bautizo} - `string`.
 * 1. {@link fecha_bautizo} - `Date`.
 * 1. {@link fecha_sacramento} - `Date`.
 * 1. {@link presbitero} - `string`.
 * 1. {@link libro} - `number`.
 * 1. {@link pagina} - `number`.
 * 1. {@link partida} - `number`.
 * 1. {@link notas} - `string`.
 */
export class Confirmacion extends Sacramento {
  /** `string` - Nombre completo de quien recibe el sacramento, separado con espacios donde sea necesario. */
  nombre: string = "";
  /** `boolean` - Sexo según: `false` para hombre, `true` para mujer. */
  sexo: boolean = false;

  /** `string` - Nombre completo del padre, separado con espacios donde sea necesario. */
  padre: string = "";
  /** `string` - Nombre completo de la madre, separado con espacios donde sea necesario. */
  madre: string = "";
  /** `string` - Nombre completo del padrino o madrina, separado con espacios donde sea necesario. */
  padrino_madrina: string = "";

  /** `string` - Parroquia donde se recibió el bautismo. */
  parroquia_bautizo: string = "";
  /** `string` - Ciudad donde se recibió el bautismo. */
  ciudad_bautizo: string = "";
  /** `Date` - Fecha de cuando se recibió el bautismo. */
  fecha_bautizo: Date = new Date();

  /**
   * Coloca todos los props automáticamente.
   * @param ini Objeto de donde obtener los datos iniciales.
   *
   * 1. Puede estar vacío y puede omitir algunos campos.
   * 2. Se toma el valor por defecto de las propiedades que no se incluyan.
   * 3. Si el tipo de dato de las propiedades no coincide, se ignora.
   * 4. Se pueden incluir propiedades de más.
   */
  constructor(ini?: AutoKeys<Confirmacion>) {
    super(ini);
    this.update(ini);
  }
}

// ---------------------------------------------------------------------- MATRIMONIOS
/**
 * Contiene toda la información relevante a los matrimonios.
 *
 * 1. {@link id} - ***readonly*** `string | null`.
 * 1. {@link novio} - `string`.
 * 1. {@link novia} - `string`.
 * 1. {@link domicilio} - `string`.
 * 1. {@link ciudad_sacramento} - `string`.
 * 1. {@link padres_novio} - `string`.
 * 1. {@link padres_novia} - `string`.
 * 1. {@link testigos} - `string[]`.
 * 1. {@link presentacion} - `string`.
 * 1. {@link fecha_sacramento} - `Date`.
 * 1. {@link presbitero} - `string`.
 * 1. {@link libro} - `number`.
 * 1. {@link pagina} - `number`.
 * 1. {@link partida} - `number`.
 * 1. {@link notas} - `string`.
 */
export class Matrimonio extends Sacramento {
  /** `string` - Nombre completo del novio, separado con espacios donde sea necesario. */
  novio: string = "";
  /** `string` - Nombre completo de la novia, separado con espacios donde sea necesario. */
  novia: string = "";

  /** `string` - Domicilio de los novios, anotado en cualquier formato. */
  domicilio: string = "";
  /** `string` - Ciudad donde se recibió el sacramento en custión. */
  ciudad_sacramento: string = "";

  /** `string` - Nombre completo de los padres del novio, separado con espacios y una coma donde sea necesario. */
  padres_novio: string = "";
  /** `string` - Nombre completo de los padres de la novia, separado con espacios y una coma donde sea necesario. */
  padres_novia: string = "";

  /** `string[]` - Lista de testigos del matrimonio. Nombre completo de los testigos, separado con espacios donde sea necesario. */
  testigos: string[] = [];
  /** `string` - Presentación matrimonial o anotaciones particulares. */
  presentacion: string = "";

  /**
   * Coloca todos los props automáticamente.
   * @param ini Objeto de donde obtener los datos iniciales.
   *
   * 1. Puede estar vacío y puede omitir algunos campos.
   * 2. Se toma el valor por defecto de las propiedades que no se incluyan.
   * 3. Si el tipo de dato de las propiedades no coincide, se ignora.
   * 4. Se pueden incluir propiedades de más.
   */
  constructor(ini?: AutoKeys<Matrimonio>) {
    super(ini);
    this.update(ini);
  }
}
// #endregion

// #region ##################################################################################### NICHOS - PERSONAS
// ---------------------------------------------------------------------- PERSONAS
/**
 * Define a todas las personas, incluyendo datos básicos como nombres, apellidos e información de contacto.
 *
 * 1. {@link id} - ***readonly*** `string | null`.
 * 1. {@link nombres} - `string`.
 * 1. {@link apellidos} - `string`.
 * 1. {@link direccion} - `string`.
 * 1. {@link telefono_celular} - `string`.
 * 1. {@link telefono_auxiliar} - `string`.
 * 1. {@link telefono_casa} - `string`.
 * 1. {@link correo} - `string`.
 * 1. {@link notas} - `string`.
 */
export abstract class Persona extends _setup {
  /** `string` - Representa el nombre de la persona, separados por espacios. */
  nombres: string = "";
  /** `string` - Representa los apellidos de la persona, separados por espacios. */
  apellidos: string = "";
  /** `string` - Indica la dirección de la persona, anotada en cualquier formato. */
  direccion: string = "";
  /** `string` - Indica el teléfono celular de contacto de la persona. */
  telefono_celular: string = "";
  /** `string` - Indica algún teléfono auxiliar de contacto de la persona. */
  telefono_auxiliar: string = "";
  /** `string` - Indica el teléfono de casa de contacto de la persona. */
  telefono_casa: string = "";
  /** `string` - Indica el correo electrónico de contacto de la persona. */
  correo: string = "";
  /** `string` - Cualquier consideración o notas adicionales. */
  notas: string = "";

  /**
   * Coloca todos los props automáticamente.
   * @param ini Objeto de donde obtener los datos iniciales.
   *
   * 1. Puede estar vacío y puede omitir algunos campos.
   * 2. Se toma el valor por defecto de las propiedades que no se incluyan.
   * 3. Si el tipo de dato de las propiedades no coincide, se ignora.
   * 4. Se pueden incluir propiedades de más.
   */
  constructor(ini?: AutoKeys<Persona>) {
    super(ini);
    this.update(ini);
  }
}

// ---------------------------------------------------------------------- BENEFICIARIOS
/**
 * Representa beneficiarios y titulares por igual. Para determinar al titular original de un nicho se usa el `orden = 0`,
 * y el resto serán beneficiarios según dicho orden.
 *
 * 1. {@link id} - ***readonly*** `string | null`.
 * 1. {@link titular_actual} - `boolean`.
 * 1. {@link orden} - `number`.
 * 1. {@link titulo_entregado} - `boolean`.
 * 1. {@link fecha_entrega} - `Date`.
 * 1. {@link notas} - `string`.
 * 1. {@link nichoID} - `string?`.
 */
export class Beneficiario extends Persona {
  /** `boolean` - Representa si el beneficiario es el actual titular del nicho en cuestión. */
  titular_actual: boolean = true;
  /** `number` - Representa el orden de prioridad para tomar posesión del nicho como titular. */
  orden: number = 0;
  /** `boolean` - Significa si el titulo ha sido otorgado o no a esta persona. */
  titulo_entregado: boolean = true;
  /** `Date` - Representa la fecha en la que se entregó el titulo. */
  fecha_entrega: Date = new Date();
  /** `string` - Cualquier consideración o notas adicionales. */
  notas: string = "";

  /** `string?` - **(ID)** Determina el nicho al cual está suscrito esta persona (sea titular o beneficiario).
   * Si es `undefined` esta persona no existe. */
  nichoID?: string;

  /**
   * Coloca todos los props automáticamente.
   * @param ini Objeto de donde obtener los datos iniciales.
   *
   * 1. Puede estar vacío y puede omitir algunos campos.
   * 2. Se toma el valor por defecto de las propiedades que no se incluyan.
   * 3. Si el tipo de dato de las propiedades no coincide, se ignora.
   * 4. Se pueden incluir propiedades de más.
   */
  constructor(ini?: AutoKeys<Beneficiario>) {
    super(ini);
    this.update(ini);
  }
}

// ---------------------------------------------------------------------- CENIZAS
/**
 * Representa el depósito de una ceniza en algún nicho. Puede haber nichos sin cenizas, pero no se puede exceder su capacidad.
 *
 * 1. {@link id} - ***readonly*** `string | null`.
 * 1. {@link nombres} - `string`.
 * 1. {@link apellidos} - `string`.
 * 1. {@link fecha_defuncion} - `Date`.
 * 1. {@link documentos} - `Documento[]`.
 * 1. {@link notas} - `string`.
 * 1. {@link nichoID} - `string?`.
 */
export class Ceniza extends _setup {
  /** `string` - Los nombres del difunto, en caso de tener más de uno se separan con espacios. */
  nombres: string = "";
  /** `string` - Los apellidos del difunto, separados por espacios. */
  apellidos: string = "";
  /** `Date` - Fecha de defunción. */
  fecha_defuncion: Date = new Date();
  /** `Documento[]` - Es un listado de todos los documentos requeridos por la parroquia. */
  documentos: Documento[] = [];
  /** `string` - Cualquier consideración o notas adicionales. */
  notas: string = "";

  /** `string?` - **(ID)** Representa el nicho donde están depositadas las cenizas. Si es `undefined` las cenizas no existen. */
  nichoID?: string;

  /**
   * Coloca todos los props automáticamente.
   * @param ini Objeto de donde obtener los datos iniciales.
   *
   * 1. Puede estar vacío y puede omitir algunos campos.
   * 2. Se toma el valor por defecto de las propiedades que no se incluyan.
   * 3. Si el tipo de dato de las propiedades no coincide, se ignora.
   * 4. Se pueden incluir propiedades de más.
   */
  constructor(ini?: AutoKeys<Ceniza>) {
    super(ini);
    this.update(ini);
  }
}

// ---------------------------------------------------------------------- EMPLEADOS
/**
 * Empleados de la parroquia, idénticos a cualquier persona, con la diferencia de estar activos o no.
 *
 * 1. {@link id} - ***readonly*** `string | null`.
 * 1. {@link activo} - `boolean`.
 * 1. {@link notas} - `string`.
 */
export class Empleado extends Persona {
  /** `boolean` - En caso de que este empleado se encuentre trabajando actualmente o no. */
  activo: boolean = true;
  /** `string` - Cualquier consideración o notas adicionales. */
  notas: string = "";

  /**
   * Coloca todos los props automáticamente.
   * @param ini Objeto de donde obtener los datos iniciales.
   *
   * 1. Puede estar vacío y puede omitir algunos campos.
   * 2. Se toma el valor por defecto de las propiedades que no se incluyan.
   * 3. Si el tipo de dato de las propiedades no coincide, se ignora.
   * 4. Se pueden incluir propiedades de más.
   */
  constructor(ini?: AutoKeys<Empleado>) {
    super(ini);
    this.update(ini);
  }
}
// #endregion

// #region ##################################################################################### NICHOS - ESTRUCTURA
// ---------------------------------------------------------------------- SECCIONES
/**
 * Representa el modelo para guardar información sobre las secciones de nichos de la parroquia. Se crearán nichos
 * respectivamente según la cantidad de filas y columnas para crear una cuadrícula.
 *
 * 1. {@link id} - ***readonly*** `string | null`.
 * 1. {@link nombre} - `string`.
 * 1. {@link orden} - `number`.
 * 1. {@link filas} - `number`.
 * 1. {@link columnas} - `number`.
 * 1. {@link color} - `string`.
 * 1. {@link notas} - `string`.
 */
export class Seccion extends _setup {
  /** `string` - Indica el nombre de la sección. */
  nombre: string = "";
  /** `number` - Indica el número de la sección (se mostrará en números romanos). */
  orden: number = 1;
  /** `number` - Indica el máximo número de filas de la sección, cada fila se enumerará con una letra según su número. */
  filas: number = 1;
  /** `number` - Indica el máximo número de columnas de la sección. */
  columnas: number = 1;
  /** `string` - Indica cualquier color con el que se asocie a esta sección. */
  color: string = "";
  /** `string` - Cualquier consideración o notas adicionales. */
  notas: string = "";

  /**
   * Coloca todos los props automáticamente.
   * @param ini Objeto de donde obtener los datos iniciales.
   *
   * 1. Puede estar vacío y puede omitir algunos campos.
   * 2. Se toma el valor por defecto de las propiedades que no se incluyan.
   * 3. Si el tipo de dato de las propiedades no coincide, se ignora.
   * 4. Se pueden incluir propiedades de más.
   */
  constructor(ini?: AutoKeys<Seccion>) {
    super(ini);
    this.update(ini);
  }
}

// ---------------------------------------------------------------------- NICHOS
/**
 * Modela la información contenida dentro de un nicho de la parroquia.
 *
 * 1. {@link id} - ***readonly*** `string | null`.
 * 1. {@link placa} - `string`.
 * 1. {@link tipo} - `string`.
 * 1. {@link precio} - `number`.
 * 1. {@link capacidad} - `number`.
 * 1. {@link fila} - `number`.
 * 1. {@link columna} - `number`.
 * 1. {@link notas} - `string`.
 * 1. {@link seccionID} - `string?`.
 * 1. {@link unido_con} - `string?`.
 * 1. {@link vendedor} - `Empleado`.
 */
export class Nicho extends _setup {
  /** `string` -  Representa lo que está escrito en la placa del nicho (generalmente el nombre). */
  placa: string = "";
  /** `string` -  Nombre del tipo de nicho que se supone que es. Esto es para autocompletar datos como el precio y la capacidad,
   * y también para poder clasificarlos de mejor manera. */
  tipo: string = "";
  /** `number` -  Cuánto cuesta este nicho en particular.
   * Si su valor es -1 tomará el precio default según el tipo de nicho al que pertenezca. */
  precio: number = 1;
  /** `number` -  Cuánto puede almacenar este nicho en particular.
   * Si su valor es -1 tomará la capacidad default según el tipo de nicho al que pertenezca. */
  capacidad: number = 1;
  /** `number` - Representa en qué fila se encuentra dentro de su sección determinada. */
  fila: number = 1;
  /** `number` - Representa en qué columna se encuentra dentro de su sección determinada. */
  columna: number = 1;
  /** `string` - Cualquier consideración o notas adicionales. */
  notas = "";

  /** `string?` - **(ID)** Representa a que sección pertenece este nicho. Si es `undefined`, el nicho no existe. */
  seccionID?: string;
  /** `string?` - **(ID)** Representa si este nicho está combinado con algún otro. En caso de tener algún valor válido, este
   * nicho hará referencia directamente hacia el otro, "cediendo" su ID. */
  unido_con?: string;
  /** `Empleado` - **(ID)** Representa quién vendió este nicho en particular. Si es `undefined` significa que no se ha vendido. */
  vendedorID?: string;

  /**
   * Coloca todos los props automáticamente.
   * @param ini Objeto de donde obtener los datos iniciales.
   *
   * 1. Puede estar vacío y puede omitir algunos campos.
   * 2. Se toma el valor por defecto de las propiedades que no se incluyan.
   * 3. Si el tipo de dato de las propiedades no coincide, se ignora.
   * 4. Se pueden incluir propiedades de más.
   */
  constructor(ini?: AutoKeys<Nicho>) {
    super(ini);
    this.update(ini);
  }
}
// #endregion

// #region ##################################################################################### NICHOS - ADMINISTRATIVO
// ---------------------------------------------------------------------- TIPOS DE NICHOS
/**
 * Determina los tipos de nichos que existen en alguna parroquia. De aquí se extrae información como su precio y capacidad.
 *
 * 1. {@link id} - ***readonly*** `string | null`.
 * 1. {@link nombre} - `string`.
 * 1. {@link capacidad} - `number`.
 * 1. {@link precio} - `number`.
 * 1. {@link notas} - `string`.
 */
export class Tipo_Nicho extends _setup {
  /** `string` - Indica el nombre del tipo de nicho, por ejemplo "sencillo" o "doble". */
  nombre: string = "";
  /** `number` - Indica la capacidad default del tipo de nicho, por ejemplo 4 o 8. */
  capacidad: number = 0;
  /** `number` - Indica el precio default del tipo de nicho, por ejemplo 2000 o 4000. */
  precio: number = 0;
  /** `string` - Cualquier consideración o notas adicionales. */
  notas: string = "";

  /**
   * Coloca todos los props automáticamente.
   * @param ini Objeto de donde obtener los datos iniciales.
   *
   * 1. Puede estar vacío y puede omitir algunos campos.
   * 2. Se toma el valor por defecto de las propiedades que no se incluyan.
   * 3. Si el tipo de dato de las propiedades no coincide, se ignora.
   * 4. Se pueden incluir propiedades de más.
   */
  constructor(ini?: AutoKeys<Tipo_Nicho>) {
    super(ini);
    this.update(ini);
  }
}

// ---------------------------------------------------------------------- DOCUMENTOS
/**
 * Información referente a los documentos requeridos por el área de nichos. Estos documentos se encuentran como una lista de
 * objetos dentro de cada ceniza y poseen información nada más de si se han entregado y cualquier otra consideración como notas.
 *
 * 1. {@link id} - ***readonly*** `string | null`.
 * 1. {@link nombre} - `string`.
 * 1. {@link entregado} - `Date?`.
 * 1. {@link notas} - `string`.
 */
export class Documento extends _setup {
  /** `string` - Representa el nombre del documento, por ejemplo "Acta de nacimiento". */
  nombre: string = "";
  /** `Date?` - Indica la fecha de entrega del documento (si no existe es que no se ha entregado). */
  entregado?: Date;
  /** `string` - Cualquier consideración o notas adicionales. */
  notas: string = "";

  /**
   * Coloca todos los props automáticamente.
   * @param ini Objeto de donde obtener los datos iniciales.
   *
   * 1. Puede estar vacío y puede omitir algunos campos.
   * 2. Se toma el valor por defecto de las propiedades que no se incluyan.
   * 3. Si el tipo de dato de las propiedades no coincide, se ignora.
   * 4. Se pueden incluir propiedades de más.
   */
  constructor(ini?: AutoKeys<Documento>) {
    super(ini);
    this.update(ini);
  }
}

// ---------------------------------------------------------------------- EXPEDIENTES
/**
 * Se emite un expediente por cada nicho vendido, pero puede haber expedientes
 * cancelados por equivocación en el folio. Los expedientes no representan personas.
 *
 * 1. {@link id} - ***readonly*** `string | null`.
 * 1. {@link folio} - `string`.
 * 1. {@link cancelado} - `boolean`.
 * 1. {@link notas} - `string`.
 * 1. {@link nichoID} - `string?`.
 */
export class Expediente extends _setup {
  /** `string` - Representa el folio del expediente, es único pero no continuo. */
  folio: string = "";
  /** `boolean` - En caso de que el expediente esté cancelado o no. */
  cancelado: boolean = false;
  /** `string` - Cualquier consideración o notas adicionales. */
  notas: string = "";

  /** `string?` - **(ID)** Representa el nicho al que está suscrito este expediente (porque puede haber más de un expediente
   * mientras estén cancelados). Si es `undefined` el expediente no existe (a menos que esté cancelado). */
  nichoID?: string;

  /**
   * Coloca todos los props automáticamente.
   * @param ini Objeto de donde obtener los datos iniciales.
   *
   * 1. Puede estar vacío y puede omitir algunos campos.
   * 2. Se toma el valor por defecto de las propiedades que no se incluyan.
   * 3. Si el tipo de dato de las propiedades no coincide, se ignora.
   * 4. Se pueden incluir propiedades de más.
   */
  constructor(ini?: AutoKeys<Expediente>) {
    super(ini);
    this.update(ini);
  }
}

// ---------------------------------------------------------------------- PAGOS
/**
 * Representa un pago al área de nichos.
 *
 * 1. {@link id} - ***readonly*** `string | null`.
 * 1. {@link folio} - `string`.
 * 1. {@link cargo} - `number`.
 * 1. {@link fecha} - `Date`.
 * 1. {@link anticipo} - `boolean`.
 * 1. {@link notas} - `string`.
 * 1. {@link nichoID} - `string?`.
 */
export class Pago extends _setup {
  /** `string` - El folio de la papeleta de pago, es único pero puede no ser continuo. */
  folio: string = "";
  /** `number` - Cuánto dinero se pagó, en número (se admiten decimales). */
  cargo: number = 1;
  /** `Date` - Día en el que se realizó el pago. */
  fecha: Date = new Date();
  /** `boolean` - Si el pago es o no un anticipo para nichos. */
  anticipo: boolean = true;
  /** `string` - Cualquier consideración o notas adicionales. */
  notas: string = "";

  /** `string?` - **(ID)** Representa el nicho al que va dirigido el pago. Si es `undefined`, el pago no existe. */
  nichoID?: string;

  /**
   * Coloca todos los props automáticamente.
   * @param ini Objeto de donde obtener los datos iniciales.
   *
   * 1. Puede estar vacío y puede omitir algunos campos.
   * 2. Se toma el valor por defecto de las propiedades que no se incluyan.
   * 3. Si el tipo de dato de las propiedades no coincide, se ignora.
   * 4. Se pueden incluir propiedades de más.
   */
  constructor(ini?: AutoKeys<Pago>) {
    super(ini);
    this.update(ini);
  }
}
// #endregion

/*
const secciones = {
  I: {
    nombre: "Piedad",
    color: "Celeste",
  },
  II: {
    nombre: "Paz",
    color: "Lima",
  },
  III: {
    nombre: "Bencidión",
    color: "Morado",
  },
  IV: {
    nombre: "Misericordia",
    color: "Rosa",
  },
  V: {
    nombre: "Fe",
    color: "Naranja",
  },
  VI: {
    nombre: "Esperanza",
    color: "Amarillo",
  },
};
*/
